import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Archive, ArchiveRestore, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function DeletionRequest({ league, call, done }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [pwd, setPwd] = useState("");
  const [reason, setReason] = useState("");
  if (league.deletion_requested_at) return (
    <div className="rounded-xl border border-red-400/20 bg-red-400/[0.05] p-3 text-xs text-red-100" data-testid="league-deletion-pending">
      <p>Suppression demandée : la ligue est archivée et l'administration de la plateforme traitera la demande.</p>
      <button onClick={async () => (await call(`/leagues/${league.id}/deletion-request/cancel`, {}, "Demande annulée")) && done(false)} className="mt-2 font-semibold text-white underline" data-testid="league-deletion-cancel">Annuler la demande</button>
    </div>
  );
  if (!open) return <button onClick={() => setOpen(true)} className="flex items-center gap-2 text-sm font-semibold text-red-300" data-testid="league-delete-start"><Trash2 size={15} /> Demander la suppression de la ligue</button>;
  const submit = async (e) => {
    e.preventDefault();
    if (await call(`/leagues/${league.id}/deletion-request`, { confirm_name: name, password: pwd, reason }, "Demande envoyée. La ligue est archivée en attendant.")) done(true);
  };
  return (
    <form onSubmit={submit} className="space-y-3 rounded-xl border border-red-400/30 bg-red-400/[0.05] p-3" data-testid="league-delete-form">
      <p className="text-xs text-red-100">La ligue est d'abord archivée (lecture seule), puis l'administration de la plateforme effectue la suppression définitive. Les comptes des membres et leurs autres ligues ne sont jamais touchés.</p>
      <label className="block text-xs text-slate-300">Tapez le nom exact de la ligue : <b>{league.name}</b>
        <input value={name} onChange={(e) => setName(e.target.value)} className="mpl-input mt-1" data-testid="league-delete-name" /></label>
      <label className="block text-xs text-slate-300">Raison (facultatif)
        <input value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} className="mpl-input mt-1" data-testid="league-delete-reason" /></label>
      <label className="block text-xs text-slate-300">Votre mot de passe
        <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} className="mpl-input mt-1" autoComplete="current-password" data-testid="league-delete-password" /></label>
      <div className="flex gap-2"><button disabled={name !== league.name || !pwd} className="rounded-full bg-red-500 px-4 py-2 text-sm font-bold text-white disabled:opacity-40" data-testid="league-delete-confirm">Envoyer la demande</button>
        <button type="button" onClick={() => setOpen(false)} className="mpl-btn-ghost">Annuler</button></div>
    </form>
  );
}

export default function LifecycleSection({ league, reload }) {
  const nav = useNavigate();
  const { loadLeagues } = useAuth();
  const [confirmArchive, setConfirmArchive] = useState(false);
  const call = async (url, body, msg) => {
    try { await api.post(url, body); toast.success(msg); return true; } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const done = async (leave) => { await loadLeagues?.(); if (leave) nav("/tableau-de-bord"); else reload(); };
  return (
    <section className="mpl-card space-y-4 border-red-400/20 p-5" data-testid="league-lifecycle">
      <h2 className="text-sm font-bold">Cycle de vie de la ligue</h2>
      {league.archived ? (
        !league.deletion_requested_at && <button onClick={async () => (await call(`/leagues/${league.id}/unarchive`, {}, "Ligue réactivée")) && done(false)} className="mpl-btn-ghost" data-testid="league-unarchive"><ArchiveRestore size={15} /> Réactiver la ligue</button>
      ) : !confirmArchive ? (
        <div><button onClick={() => setConfirmArchive(true)} className="mpl-btn-ghost" data-testid="league-archive"><Archive size={15} /> Archiver la ligue</button>
          <p className="mt-1 text-xs text-slate-400">La ligue devient consultable seulement : historique conservé, plus de pronostics, défis, messages ni invitations.</p></div>
      ) : (
        <div className="rounded-xl border border-amber-400/20 bg-amber-400/[0.05] p-3 text-xs text-amber-100">
          <p>Archiver « {league.name} » ? Les invitations actives seront révoquées. Vous pourrez la réactiver plus tard.</p>
          <div className="mt-2 flex gap-2"><button onClick={async () => (await call(`/leagues/${league.id}/archive`, {}, "Ligue archivée")) && done(false)} className="mpl-btn-primary" data-testid="league-archive-confirm">Confirmer l'archivage</button>
            <button onClick={() => setConfirmArchive(false)} className="mpl-btn-ghost">Annuler</button></div>
        </div>
      )}
      <DeletionRequest league={league} call={call} done={done} />
    </section>
  );
}
